import { useEffect, useRef, useState } from 'react';
import LogoSpin from '../../../shared/animations/LogoSpin';
import './OAuthCallback.css';
import './PostLoginLoader.css';

const STEPS = [
  { id: 1, label: 'Verified' },
  { id: 2, label: 'Loading session' },
  { id: 3, label: 'Checking setup' },
  { id: 4, label: 'Ready' },
];

// Hand-composed trace layout (Q from the wireframe review): varied length
// and direction — long sweeps, short stubs, corner-anchored pieces — kept
// non-overlapping by giving each trace its own y-band and x-range within
// that band. Coordinates are fractions of viewport width/height so the
// layout scales to any screen size. Every corner is a pure 90° bend
// (no diagonals) via squareOff below.
// Hand-composed layout (Q from the wireframe review): varied length and
// direction, non-overlapping. The actual reserved clearance around the
// content (badge/logo/headline/tracker) is computed at runtime from the
// content element's real measured bounding box (see CONTENT_PADDING below)
// rather than guessed viewport fractions, since the tracker row's width
// depends on rendered text and doesn't line up with any fixed fraction.
const CONTENT_PADDING = 28; // px of breathing room around the measured content box

const TRACE_TEMPLATES = [
  // long sweep, top-left down the left side then along the bottom edge,
  // own lane below the tracker row, dropping down outside the tracker's
  // wider x-range before cutting across
  [[0, 0.09], [0.1, 0.09], [0.1, 0.92], [0.933, 0.92]],
  // short horizontal, mid-right, dips down closer to the zone's top-right shoulder
  [[0.633, 0.18], [0.72, 0.18], [0.72, 0.06], [1, 0.06]],
  // vertical-leaning, bottom-left going up the far left edge, own lane at
  // x=0.05 so it never shares the x=0.1 lane with the top-left sweep
  [[0.067, 1], [0.067, 0.75], [0.05, 0.75], [0.05, 0.311], [0.28, 0.311]],
  // short, top-right corner, comes in close along the zone's top edge
  [[0.933, 0], [0.933, 0.19], [0.64, 0.19]],
  // medium, mid-left, angling right then down — own lane, grazes the zone's left shoulder
  [[0, 0.45], [0.36, 0.45], [0.36, 0.87], [0.7, 0.87]],
  // short vertical stub, upper-middle, above the center zone
  [[0.5, 0], [0.5, 0.1], [0.58, 0.1]],
  // long, bottom edge sweeping up to top-right, own lane on the far right
  [[0.78, 1], [0.85, 1], [0.85, 0.133], [1, 0.133]],
  // medium, upper-left, sweeping right and down, above the center zone
  [[0, 0.02], [0.3, 0.02], [0.3, 0.13], [0.517, 0.13]],
  // short, lower-right corner, own lane below the tracker row
  [[1, 0.689], [0.867, 0.689], [0.867, 0.82], [0.64, 0.82]],
  // medium vertical, right side going down, comes in close along the zone's right edge
  [[0.85, 0], [0.85, 0.5], [0.7, 0.5], [0.7, 0.6]],
  // short, mid-bottom, own lane at the very bottom edge, clear of the others
  [[0.4, 1], [0.4, 0.97], [0.583, 0.97]],
  // long sweep, upper-right to left edge, staying above the center zone
  [[1, 0.111], [0.72, 0.111], [0.72, 0.16], [0.083, 0.16]],
  // short, far left, own band above the vertical-leaning trace
  [[0, 0.42], [0.15, 0.42], [0.15, 0.46], [0.28, 0.46]],
  // medium, far right, own band below the top-right corner trace, clear of the zone
  [[1, 0.35], [0.94, 0.35], [0.94, 0.55], [0.85, 0.55]],
  // short vertical, lower-left corner, own lane clear of the bottom-edge sweep
  [[0.05, 1], [0.05, 0.85], [0.12, 0.85]],
  // medium, far left, fills the gap between the mid-left trace and the lower-left corner,
  // clearly below the tracker row
  [[0, 0.78], [0.15, 0.78], [0.15, 0.82], [0.24, 0.82]],
  // long, upper area, sweeping wide above the center zone
  [[0.6, 0], [0.6, 0.06], [0.78, 0.06], [0.78, 0.1]],
  // medium, mid-lower area, own lane clear of the other bottom traces
  [[0, 0.95], [0.15, 0.95], [0.15, 0.98], [0.24, 0.98]],
  // short, far right lower corner, own lane below existing right-side traces
  [[1, 0.9], [0.94, 0.9], [0.94, 0.95], [0.86, 0.95]],
  // short, top-middle-left, above the center zone
  [[0.35, 0], [0.35, 0.045], [0.44, 0.045], [0.44, 0.075]],
  // short, far top-right corner, fills the sparse upper-right area
  [[1, 0.02], [0.97, 0.02], [0.97, 0.09], [0.9, 0.09]],
  // medium, top-right, own lane between the two existing right-side upper traces
  [[1, 0.24], [0.96, 0.24], [0.96, 0.3], [0.9, 0.3]],
  // short, far top-left corner, fills the sparse upper-left area
  [[0, 0.28], [0.06, 0.28], [0.06, 0.35], [0.12, 0.35]],
  // medium, upper-left, own lane below the top-left sweep, kept left of the
  // x=0.1 lane used by the long left-side sweep
  [[0, 0.16], [0.04, 0.16], [0.04, 0.22], [0.08, 0.22]],
  // short, mid-right, fills the gap around the middle of the right edge
  [[1, 0.75], [0.96, 0.75], [0.96, 0.8], [0.9, 0.8]],
  // short, upper-mid-right, between the top-right corner trace and mid-right traces
  [[0.78, 0.25], [0.78, 0.3], [0.83, 0.3]],
];

const NODE_GAP = 9; // px of line trimmed back from each endpoint node

// Returns a copy of a cumulative-length-indexed path trimmed inward by
// `gap` px at both ends, so the drawn line stops short of the endpoint
// nodes instead of running through their centers.
const trimPath = (path, cum, length, gap) => {
  const from = Math.min(gap, length / 2);
  const to = Math.max(length - gap, length / 2);

  const pointAt = (dist) => {
    for (let i = 1; i < cum.length; i++) {
      if (dist <= cum[i]) {
        const segLen = cum[i] - cum[i - 1] || 1;
        const t = (dist - cum[i - 1]) / segLen;
        const a = path[i - 1], b = path[i];
        return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
      }
    }
    return path[path.length - 1];
  };

  const trimmed = [pointAt(from)];
  for (let i = 1; i < path.length - 1; i++) {
    if (cum[i] > from && cum[i] < to) trimmed.push(path[i]);
  }
  trimmed.push(pointAt(to));
  return trimmed;
};

// Convert any diagonal move into two perpendicular (horizontal-then-vertical)
// moves, so every rendered segment is purely axis-aligned — pure 90° bends,
// no 45° chamfers.
const squareOff = (template) => {
  const out = [template[0]];
  for (let i = 1; i < template.length; i++) {
    const [px, py] = out[out.length - 1];
    const [x, y] = template[i];
    if (px !== x && py !== y) {
      out.push([x, py]);
    }
    out.push([x, y]);
  }
  return out;
};

const PostLoginLoaderCircuits = () => {
  const bgCanvasRef = useRef(null);
  const contentRef = useRef(null);
  const animFrameRef = useRef(null);
  const [activeStep, setActiveStep] = useState(2);

  useEffect(() => {
    const canvas = bgCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H;
    let traces = [];
    let contentBox = null;

    const measureContent = () => {
      const el = contentRef.current;
      if (!el) { contentBox = null; return; }
      const rect = el.getBoundingClientRect();
      contentBox = {
        x0: rect.left - CONTENT_PADDING,
        x1: rect.right + CONTENT_PADDING,
        y0: rect.top - CONTENT_PADDING,
        y1: rect.bottom + CONTENT_PADDING,
      };
    };

    const buildBoard = () => {
      const cx = W / 2, cy = H * 0.42;
      const maxDist = Math.hypot(Math.max(cx, W - cx), Math.max(cy, H - cy));

      traces = TRACE_TEMPLATES.map((template, i) => {
        const path = squareOff(template).map(([fx, fy]) => ({ x: fx * W, y: fy * H }));

        let length = 0;
        const cum = [0];
        for (let j = 1; j < path.length; j++) {
          length += Math.hypot(path[j].x - path[j - 1].x, path[j].y - path[j - 1].y);
          cum.push(length);
        }

        // How close this trace's midpoint sits to the headline center —
        // used to fade the traveling highlight/node flash toward the edges
        // so the text stays the focal point. 1 = at center, 0 = far edge.
        const mid = path[Math.floor(path.length / 2)];
        const dist = Math.hypot(mid.x - cx, mid.y - cy);
        const centerFactor = 1 - Math.min(1, dist / maxDist);

        return {
          path,
          cum,
          length: Math.max(1, length),
          drawPath: trimPath(path, cum, length, NODE_GAP),
          progress: -0.3 - (i / TRACE_TEMPLATES.length) * 1.6,
          speed: 0.0022 + (i % 3) * 0.0006,
          pulseLen: 0.22,
          centerFactor,
        };
      });
    };

    function resize() {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
      buildBoard();
      // Measure after layout settles from the resize.
      requestAnimationFrame(measureContent);
    }
    resize();
    window.addEventListener('resize', resize);

    let t = 0;

    function draw() {
      t++;
      ctx.clearRect(0, 0, W, H);

      ctx.lineJoin = 'miter';
      ctx.lineCap = 'round';

      ctx.save();
      if (contentBox) {
        // Clip out the measured content box (badge/logo/headline/tracker)
        // so no trace, node, or pulse ever renders on top of it, regardless
        // of how wide any given row of text actually is.
        ctx.beginPath();
        ctx.rect(0, 0, W, H);
        ctx.rect(contentBox.x1, contentBox.y0, contentBox.x0 - contentBox.x1, contentBox.y1 - contentBox.y0);
        ctx.clip('evenodd');
      }

      // Base trace lines — dim, static; stops short of each endpoint node
      // so the line doesn't visibly run through the node's center
      traces.forEach((tr) => {
        ctx.beginPath();
        tr.drawPath.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
        ctx.strokeStyle = 'rgba(176,79,240,0.14)';
        ctx.lineWidth = 1.4;
        ctx.stroke();
      });

      // Slow traveling glow pulse along each trace
      traces.forEach((tr) => {
        tr.progress += tr.speed;
        if (tr.progress > 1 + tr.pulseLen) tr.progress = -tr.pulseLen - 0.5;

        const head = Math.min(1, Math.max(0, tr.progress));
        const tail = Math.min(1, Math.max(0, tr.progress - tr.pulseLen));
        tr.headFrac = head;
        if (head <= 0 || tail >= 1) return;

        // Clamp to the same trimmed range as the base line so the pulse
        // also stops short of the endpoint nodes instead of reaching them.
        const minLen = Math.min(NODE_GAP, tr.length / 2);
        const maxLen = Math.max(tr.length - NODE_GAP, tr.length / 2);
        const headLen = Math.min(maxLen, Math.max(minLen, head * tr.length));
        const tailLen = Math.min(maxLen, Math.max(minLen, tail * tr.length));

        ctx.beginPath();
        let started = false;
        for (let i = 1; i < tr.path.length; i++) {
          const segFrom = tr.cum[i - 1], segTo = tr.cum[i];
          const from = Math.max(segFrom, tailLen);
          const to = Math.min(segTo, headLen);
          if (from >= to) continue;
          const a = tr.path[i - 1], b = tr.path[i];
          const segLen = segTo - segFrom || 1;
          const lf = (from - segFrom) / segLen;
          const lt = (to - segFrom) / segLen;
          const sx = a.x + (b.x - a.x) * lf, sy = a.y + (b.y - a.y) * lf;
          const ex = a.x + (b.x - a.x) * lt, ey = a.y + (b.y - a.y) * lt;
          if (!started) { ctx.moveTo(sx, sy); started = true; }
          ctx.lineTo(ex, ey);
        }
        const nearness = 0.3 + tr.centerFactor * 0.7;
        ctx.strokeStyle = `rgba(176,79,240,${0.45 * nearness})`;
        ctx.lineWidth = 2.1;
        ctx.shadowColor = '#9404C3';
        ctx.shadowBlur = 4 * nearness;
        ctx.stroke();
        ctx.shadowBlur = 0;
      });

      // Endpoint nodes — dim by default, flash bright only when the pulse
      // arrives at that end (start point never flashes — pulse departs
      // there); flash strength fades toward the edges, staying strongest
      // near the headline in the center.
      traces.forEach((tr) => {
        const endProximity = 1 - Math.min(1, Math.abs(tr.headFrac - 1) / tr.pulseLen);
        const nearness = 0.3 + tr.centerFactor * 0.7;
        const ends = [
          { p: tr.path[0], hit: 0 },
          { p: tr.path[tr.path.length - 1], hit: Math.max(0, endProximity) * nearness },
        ];
        ends.forEach(({ p, hit }) => {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 5 + hit * 1.5, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(176,79,240,${0.14 + hit * 0.66})`;
          ctx.lineWidth = 1.4 + hit * 0.6;
          if (hit > 0.1) {
            ctx.shadowColor = '#9404C3';
            ctx.shadowBlur = hit * 10;
          }
          ctx.stroke();
          ctx.shadowBlur = 0;
        });
      });

      ctx.restore();

      // Ambient core glow behind the ring logo/headline
      const cx = W / 2, cy = H * 0.42;
      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 300);
      coreGrad.addColorStop(0, 'rgba(148,4,195,0.12)');
      coreGrad.addColorStop(1, 'rgba(148,4,195,0)');
      ctx.fillStyle = coreGrad;
      ctx.beginPath(); ctx.arc(cx, cy, 300, 0, Math.PI * 2); ctx.fill();

      animFrameRef.current = requestAnimationFrame(draw);
    }
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  useEffect(() => {
    const t1 = setTimeout(() => setActiveStep(3), 1200);
    const t2 = setTimeout(() => setActiveStep(4), 2800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div className="oauth-callback-overlay pll-overlay">
      <canvas ref={bgCanvasRef} className="oauth-bg-canvas" />
      <div className="oauth-glow-center pll-glow" />

      {/* Corner accents */}
      <div className="pll-corner pll-corner-tl" />
      <div className="pll-corner pll-corner-tr" />
      <div className="pll-corner pll-corner-bl" />
      <div className="pll-corner pll-corner-br" />

      <div className="oauth-callback-container pll-container" ref={contentRef}>

        <div className="pll-badge">
          <span className="pll-badge-dot" />
          <span className="pll-badge-text">Tonic Suite</span>
        </div>

        <div className="pll-hex-wrap">
          <LogoSpin size={190} />
        </div>

        <div className="oauth-headline pll-headline">
          <h1>
            Setting up your <span className="oauth-accent">session</span>
            <span className="oauth-dots"><span>.</span><span>.</span><span>.</span></span>
          </h1>
          <p className="pll-sub">Hang tight — we're getting everything ready for you</p>
        </div>

        <div className="oauth-track pll-track">
          {STEPS.map((step, i) => {
            const isDone = step.id < activeStep;
            const isActive = step.id === activeStep;
            const stepClass = isDone
              ? 'oauth-track-step done'
              : isActive
              ? 'oauth-track-step active'
              : 'oauth-track-step';
            const lineClass = step.id < activeStep
              ? 'oauth-track-line lit pll-line-lit'
              : 'oauth-track-line';
            return (
              <div key={step.id} style={{ display: 'contents' }}>
                <div className={stepClass}>
                  <div className="oauth-track-dot pll-dot">
                    {isDone && <span className="pll-dot-check">✓</span>}
                  </div>
                  <div className="oauth-track-label">{step.label}</div>
                </div>
                {i < STEPS.length - 1 && <div className={lineClass} />}
              </div>
            );
          })}
        </div>
      </div>

      <div className="oauth-footer pll-footer">
        <span className="oauth-footer-brand">TONIC</span>
        &nbsp;·&nbsp; Secured &nbsp;·&nbsp; End-to-End Encrypted
      </div>
    </div>
  );
};

export default PostLoginLoaderCircuits;
